import { Extractor } from '@import-io/types';
import { IExtendedExtractorVersion, IExtractorVersion, ILegacyExtractor } from '_/types/extractors/extractors.types';
import { ISource } from '_/types/sources/source.types';
import { formatDateTime } from '_/utils/dateUtils';
import zeusSdk from '_/utils/SDKs';

const extractorVersionsAdapter = (versions: IExtractorVersion[] = []): IExtendedExtractorVersion[] => {
  const lastIndex = versions.length - 1;
  return versions.map((version, index) => ({
    ...version,
    formattedTimestamp: formatDateTime(version.addedAt, 'LLL'),
    previousTag: index < lastIndex ? versions[index + 1].tag : null,
  }));
};

export const fetchExtractorVersions = async (extractorId: string): Promise<IExtractorVersion[]> => {
  try {
    const response = await zeusSdk.extractors.getChildren(extractorId, 'extractorversions', {
      include: 'editor',
      queryParameters: { sortDirection: 'DESC', sort: 'addedAt' },
    });
    // Apply adapter to format timestamps and add previousTag
    return extractorVersionsAdapter(response?.items || []);
  } catch (error) {
    console.error('Error fetching versions for extractor', error);
    return [];
  }
};

export const fetchLegacyExtractor = async (extractorId: string): Promise<ILegacyExtractor | null> => {
  try {
    const legacyExtractor = await zeusSdk.legacyExtractors.get(extractorId);
    return legacyExtractor;
  } catch (error) {
    console.error('Error fetching selected extractor', error);
    return null;
  }
};

export const fetchExtractor = async (extractorId: string): Promise<Extractor | null> => {
  try {
    const extractor = await zeusSdk.extractors.get(extractorId);
    return extractor;
  } catch (error) {
    console.error('Error fetching extractor', error);
    return null;
  }
};

export const fetchExtractorVersion = async (
  extractorVersionId: string,
  allVersions: any[] = [],
): Promise<IExtendedExtractorVersion | null> => {
  try {
    const selectedExtractorVersion = await zeusSdk.extractorVersions.get(extractorVersionId, {
      include: 'editor',
    });

    // Find previous tag if versions list is provided
    if (allVersions.length > 0) {
      const currentVersionIndex = allVersions.findIndex(({ id }: any) => id === extractorVersionId);
      const previousTag = currentVersionIndex < allVersions.length - 1 ? allVersions[currentVersionIndex + 1].tag : null;
      return { ...selectedExtractorVersion, previousTag: previousTag };
    }

    return selectedExtractorVersion;
  } catch (error) {
    console.error('Error fetching extractor version', error);
    return null;
  }
};

export const fetchOrgExtractors = async (
  orgId: string,
  cursor?: string,
): Promise<{ items: Extractor[]; cursor: string | null }> => {
  try {
    const options = {
      queryParameters: {
        ...(cursor && { cursor: cursor }),
        limit: 100,
        sort: 'addedAt',
        sortDirection: 'DESC',
      },
      include: 'editor',
    };

    const { items, metadata } = await zeusSdk.orgs.getChildren(orgId, 'extractors', options);
    return {
      items: items || [],
      cursor: metadata?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching extractors for org', error);
    return { items: [], cursor: null };
  }
};

export const fetchExtractorSourcesForExtractor = async (
  extractorId: string,
  cursor?: string,
): Promise<{ items: ISource[]; cursor: string | null }> => {
  try {
    const options = {
      include: 'assignedUser,collection.project',
      queryParameters: {
        ...(cursor && { cursor: cursor }),
        limit: 20,
        sort: 'name',
      },
    };
    const { items, metadata } = await zeusSdk.extractors.getChildren(extractorId, 'sources', options);
    return {
      items: items || [],
      cursor: metadata?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching extractor sources', error);
    return { items: [], cursor: null };
  }
};

export const fetchExtractorVersionsForExtractor = async (
  extractorId: string,
  cursor?: string,
): Promise<{ items: IExtractorVersion[]; cursor: string | null }> => {
  try {
    const options = {
      include: 'editor',
      queryParameters: {
        ...(cursor && { cursor: cursor }),
        limit: 20,
        sort: 'addedAt',
        sortDirection: 'DESC',
      },
    };

    const { items, metadata } = await zeusSdk.extractors.getChildren(extractorId, 'extractorversions', options);
    const versions = extractorVersionsAdapter(items || []);
    return {
      items: versions,
      cursor: metadata?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching extractor versions', error);
    return { items: [], cursor: null };
  }
};
